import { useEffect, useState, useCallback } from 'react';
import { HelpButton } from '../../components/HelpButton';
import { ConfirmDialog, Pagination } from '../../components';
import { staffTagsService, STAFF_PAGE_SIZE } from '../../lib/api-service';
import type { Tag } from '../../lib/types';
import { IconPicker, IconByName } from '../../components/IconPicker';
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  Tag as TagIcon,
  Star,
  X,
  Loader2,
  Info,
} from 'lucide-react';

type TagFormData = {
  name: string;
  icon: string;
  is_main_tag: boolean;
  color: string;
};

const EMPTY_FORM: TagFormData = { name: '', icon: '', is_main_tag: false, color: '' };

export default function ManageTags() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'main' | 'other'>('all');

  // Form state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<TagFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Tag | null>(null);

  const loadTags = useCallback(async () => {
    setLoading(true);
    try {
      const params: { page?: number; search?: string } = { page };
      if (searchQuery) params.search = searchQuery;
      const data = await staffTagsService.list(params);
      setTags(data.results);
      setTotalCount(data.count);
    } catch {
      setError('Nu s-au putut încărca tag-urile.');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery]);

  useEffect(() => { loadTags(); }, [loadTags]);
  useEffect(() => { setPage(1); }, [searchQuery]);

  function startAdd() {
    setIsAdding(true);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  }

  function startEdit(tag: Tag) {
    setEditingId(tag.id);
    setIsAdding(false);
    setForm({ name: tag.name, icon: tag.icon, is_main_tag: tag.is_main_tag, color: tag.color });
    setError('');
  }

  function cancelForm() {
    setIsAdding(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setError('Denumirea este obligatorie.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isAdding) {
        await staffTagsService.create(form);
      } else if (editingId) {
        await staffTagsService.update(editingId, form);
      }
      cancelForm();
      await loadTags();
    } catch {
      setError('Eroare la salvare. Verifică datele și încearcă din nou.');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await staffTagsService.delete(deleteTarget.id);
      setDeleteTarget(null);
      await loadTags();
    } catch {
      setError('Eroare la ștergere.');
    }
  }

  // Filter type is applied client-side since total set is small
  const filteredTags = filterType === 'all' ? tags
    : tags.filter((t) => filterType === 'main' ? t.is_main_tag : !t.is_main_tag);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
            Tag-uri
          </h1>
          <HelpButton to="/staff/documentatie/taguri" />
        </div>
        {!isAdding && editingId === null && (
          <button
            onClick={startAdd}
            className="flex items-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90"
            style={{ fontFamily: 'var(--font-primary)' }}
          >
            <Plus className="h-4 w-4" />
            Tag Nou
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Add / Edit Form */}
      {(isAdding || editingId !== null) && (
        <div className="mb-6 rounded-xl border bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
              {isAdding ? 'Tag Nou' : 'Editează Tag'}
            </h2>
            <button onClick={cancelForm} className="text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Denumire *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Workshop"
                autoFocus
                className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                style={{ fontFamily: 'var(--font-secondary)' }}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                Iconiță
              </label>
              <IconPicker value={form.icon} onChange={(icon) => setForm({ ...form, icon })} />
              {form.icon && (
                <button type="button" onClick={() => setForm({ ...form, icon: '' })} className="mt-1 text-xs text-muted-foreground hover:text-foreground">
                  Șterge iconița
                </button>
              )}
            </div>
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_main_tag}
                  onChange={(e) =>
                    setForm({ ...form, is_main_tag: e.target.checked, color: e.target.checked ? form.color : '' })
                  }
                  className="h-4 w-4 rounded"
                  style={{ accentColor: 'var(--vivid-purple)' }}
                />
                <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                  Tag Principal
                </span>
              </label>
              <p className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                Tag-urile principale apar ca puncte colorate în calendar, filtre rapide în lista de evenimente și badge-uri pe cardurile de activități.
              </p>
            </div>
            {form.is_main_tag && (
              <div>
                <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                  Culoare *
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={form.color || '#3b0b86'}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="h-10 w-10 cursor-pointer rounded border-0"
                  />
                  <input
                    type="text"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    placeholder="#3b0b86"
                    maxLength={7}
                    className="w-28 rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
                    style={{ fontFamily: 'var(--font-secondary)' }}
                  />
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button
                onClick={cancelForm}
                className="rounded-lg border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-gray-50"
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                Anulează
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg bg-[var(--vivid-purple)] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                {saving ? 'Se salvează...' : isAdding ? 'Adaugă' : 'Salvează'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            placeholder="Caută tag-uri..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
            style={{ fontFamily: 'var(--font-secondary)' }}
          />
        </div>
        <div className="flex gap-1.5">
          {([
            { key: 'all' as const, label: 'Toate' },
            { key: 'main' as const, label: 'Principale' },
            { key: 'other' as const, label: 'Alte' },
          ]).map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterType(f.key)}
              className={`rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                filterType === f.key
                  ? 'bg-[var(--vivid-purple)] text-white'
                  : 'bg-white text-muted-foreground hover:bg-gray-50'
              }`}
              style={{ fontFamily: 'var(--font-primary)' }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tags Table */}
      <div className="overflow-hidden rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50/50">
              <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Denumire</th>
              <th className="hidden px-4 py-3 text-left font-semibold sm:table-cell" style={{ fontFamily: 'var(--font-primary)' }}>Iconiță</th>
              <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Tip</th>
              <th className="px-4 py-3 text-left font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Culoare</th>
              <th className="px-4 py-3 text-right font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {filteredTags.map((tag) => (
              <tr key={tag.id} className="border-b last:border-b-0 hover:bg-gray-50/50">
                <td className="px-4 py-3 font-medium" style={{ fontFamily: 'var(--font-primary)' }}>
                  {tag.name}
                </td>
                <td className="hidden px-4 py-3 sm:table-cell">
                  {tag.icon ? (
                    <IconByName name={tag.icon} className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {tag.is_main_tag ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--vivid-orange)]/10 px-2 py-0.5 text-xs font-medium text-[var(--vivid-orange)]">
                      <Star className="h-3 w-3" />
                      Principal
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      <TagIcon className="h-3 w-3" />
                      Normal
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {tag.color ? (
                    <div className="flex items-center gap-2">
                      <div className="h-5 w-5 rounded-full border" style={{ backgroundColor: tag.color }} />
                      <span className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                        {tag.color}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => startEdit(tag)}
                      className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-gray-100 hover:text-[var(--vivid-purple)]"
                      title="Editează"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(tag)}
                      className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                      title="Șterge"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredTags.length === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <TagIcon className="mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
              Nu am găsit tag-uri
            </p>
          </div>
        )}
      </div>

      <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Șterge tag-ul"
        description={`Sigur vrei să ștergi tag-ul „${deleteTarget?.name}"? Acțiunea este ireversibilă.`}
      />
    </div>
  );
}
